import StaticPage from "../components/StaticPage";
import useSEO from "../hooks/useSEO";

export default function AboutPage() {
  useSEO({
    title: "About GetDeparto | Verifiable Flight Reservations for Visa Applications",
    description:
      "GetDeparto is operated by Get Departo — Bengaluru-based specialists in temporary flight reservations for visa applications and onward travel proof.",
    canonical: "https://getdeparto.com/about",
    og: {
      title: "About GetDeparto | Verifiable Flight Reservations",
      description:
        "Bengaluru-based specialists in temporary flight reservations with a live PNR — built for visa applications and onward travel proof.",
      url: "https://getdeparto.com/about",
      image: "https://getdeparto.com/og-image.png",
      type: "website",
      siteName: "GetDeparto",
    },
  });
  return (
    <StaticPage
      title="About getdeparto"
      subtitle="A small, focused team helping travellers meet visa and onward-travel documentation requirements - without paying for a ticket they might not use."
      updated="April 2026"
    >
      <section data-testid="about-intro">
        <h2>Who we are</h2>
        <p>
          getdeparto is operated by <strong>Get Departo</strong>, a
          company based in Bengaluru, India. We build simple travel tools for people
          applying for visas and organising international journeys.
        </p>
      </section>

      <section data-testid="about-what">
        <h2>What we do</h2>
        <p>
          We provide <strong>temporary, verifiable flight reservations</strong>{" "}
          with a live PNR, intended for visa applications and travel planning.
          A reservation is a held booking - not a paid ticket - that is
          checkable on the airline's own system during its validity window.
        </p>
      </section>

      <section data-testid="about-problem">
        <h2>The problem we solve</h2>
        <p>
          Most visa applications ask for a flight itinerary as part of the
          documentation. Purchasing a full ticket before visa approval is
          expensive, risky, and often advised against by embassies themselves.
          A temporary reservation is a lightweight, reasonable alternative that
          satisfies the documentation step without committing to a paid ticket.
        </p>
      </section>

      <section data-testid="about-services">
        <h2>What we offer</h2>
        <ul>
          <li>Flight reservations with a live PNR</li>
          <li>Clean PDF itineraries with passenger and flight details</li>
          <li>Delivery via WhatsApp and email</li>
          <li>Reissue or refund support if a PNR is not verifiable</li>
        </ul>
      </section>

      <section data-testid="about-usecases">
        <h2>Common use cases</h2>
        <ul>
          <li>Schengen visa applications</li>
          <li>Japan visa applications</li>
          <li>UAE and other GCC visa applications</li>
          <li>Proof of onward travel at immigration checkpoints</li>
          <li>Supporting documentation for study, work, or visitor visas</li>
        </ul>
      </section>

      <section data-testid="about-how">
        <h2>How we create reservations</h2>
        <p>
          Reservations are created through licensed travel partners and
          standard booking systems wherever possible. Availability and validity
          depend on airline inventory and system policies at the time the
          reservation is made.
        </p>
      </section>

      <section
        data-testid="about-disclaimer"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 22,
          marginTop: 16,
        }}
      >
        <h2 style={{ marginTop: 0 }}>Important disclosures</h2>
        <ul style={{ margin: 0 }}>
          <li>
            Our reservations are <strong>temporary</strong> and are{" "}
            <strong>not paid issued tickets</strong>. They cannot be used for
            actual travel.
          </li>
          <li>
            We do <strong>not</strong> guarantee visa approval. Visa decisions
            are made entirely by embassies and consulates.
          </li>
          <li>
            We do <strong>not</strong> produce false, altered, or fraudulent
            documents. Every reservation we deliver is a genuine held booking
            with a real PNR during its validity period.
          </li>
        </ul>
      </section>

      <section data-testid="about-contact" style={{ marginTop: 24 }}>
        <h2>Get in touch</h2>
        <p>
          Questions, reissues, or refunds? Reach us on WhatsApp at{" "}
          <a className="gd-link" href="https://wa.me/919036705707">
            +91 90367 05707
          </a>{" "}
          24 × 7, or email{" "}
          <a className="gd-link" href="mailto:support@getdeparto.com">
            support@getdeparto.com
          </a>
          .
        </p>
      </section>
    </StaticPage>
  );
}
